import { useCallback, useEffect, useRef, useState } from "react";

import { anotar, olvidar, pendiente, type Grabacion } from "./cajon";
import { conDuracionReal } from "./webm";

/**
 * **La grabadora de «Grabar mejora»** (M13): pantalla + micrófono en un solo `MediaStream`, grabado
 * con `MediaRecorder` del navegador (sin librerías). Con tope: al llegar a `maxSegundos` se detiene
 * sola y la grabación queda lista para guardar.
 *
 * ```
 * lista → grabando (contador) → grabada (el archivo, para la ficha) → lista
 *   ↑            ↘ error (no eligió pantalla, sin micrófono en «solo voz»…)
 *   └ al abrir: una grabación que quedó en el cajón → grabada (recuperada)
 * ```
 *
 * **Nunca se pierde** (H6): cada trozo de 5 s va al cajón del navegador (`cajon.ts`) apenas llega,
 * y sale de ahí solo cuando el servidor la guardó o la persona la descartó. Si se recarga o se cierra
 * a media grabación, al volver se ofrece con lo grabado hasta el último trozo. Al cerrar, el archivo
 * lleva su duración real en la cabecera (`webm.ts`).
 *
 * Si el navegador no deja grabar la pantalla (el celular), se graba solo la voz. Si niega el
 * micrófono, se graba la pantalla sin voz y el contador lo dice.
 */

export type EstadoGrabadora =
  | { fase: "lista" }
  | { fase: "grabando"; segundos: number; conVoz: boolean; soloVoz: boolean }
  | {
      fase: "grabada";
      id: string;
      archivo: Blob;
      segundos: number;
      porTope: boolean;
      ruta: string;
      /** Venía del cajón al abrir: «cortada» si se cerró grabando, «detenida» si faltó guardarla. */
      recuperada?: "cortada" | "detenida";
    }
  | { fase: "error"; mensaje: string };

/** ~1 Mbps de video: 10 minutos ≈ 80 MB, debajo del tope de 100 MB por petición de Cloudflare. */
const VIDEO_BPS = 1_000_000;
const AUDIO_BPS = 64_000;
/** Cada cuánto entrega un trozo `MediaRecorder`: lo más que se pierde si se cae la pestaña. */
const TROZO_MS = 5_000;
const TIPOS = {
  pantalla: ["video/webm;codecs=vp8,opus", "video/webm", "video/mp4"],
  voz: ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"],
};

/** 192 → «3:12». */
export const reloj = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export const puedeGrabarPantalla =() => typeof navigator.mediaDevices?.getDisplayMedia === "function";
export const puedeGrabar = () => typeof MediaRecorder !== "undefined" && typeof navigator.mediaDevices?.getUserMedia === "function";

function tipoPara(soloVoz: boolean): string | undefined {
  return TIPOS[soloVoz ? "voz" : "pantalla"].find((t) => MediaRecorder.isTypeSupported?.(t));
}

type Partes = {
  recorder: MediaRecorder | null;
  streams: MediaStream[];
  trozos: Blob[];
  reloj: ReturnType<typeof setInterval> | undefined;
  grabacion: Grabacion | null;
};

/** La fila del cajón de una grabación que empieza ahora (`tipo` sin sus códecs). */
function nuevaGrabacion(mime: string, ruta: string): Grabacion {
  const inicio = Date.now();
  return { id: crypto.randomUUID(), inicio, ultimo: inicio, tipo: mime.split(";")[0] || "video/webm", ruta, porTope: false };
}

/** Pide confirmación al recargar o cerrar mientras graba («¿Salir del sitio?»). */
const avisarAlSalir = (e: BeforeUnloadEvent) => e.preventDefault();

export function useGrabadora(maxSegundos: number) {
  const [estado, setEstado] = useState<EstadoGrabadora>({ fase: "lista" });
  const partes = useRef<Partes>({ recorder: null, streams: [], trozos: [], reloj: undefined, grabacion: null });
  const tope = useRef(maxSegundos);
  tope.current = maxSegundos;
  const actual = useRef(estado);
  actual.current = estado;

  /** Suelta la cámara de la pantalla y el micrófono: el navegador deja de mostrar «compartiendo». */
  const soltar = useCallback(() => {
    const p = partes.current;
    clearInterval(p.reloj);
    p.reloj = undefined;
    for (const s of p.streams) for (const t of s.getTracks()) t.stop();
    p.streams = [];
    window.removeEventListener("beforeunload", avisarAlSalir);
  }, []);

  const detener = useCallback((porTope = false) => {
    const p = partes.current;
    clearInterval(p.reloj);
    if (p.grabacion) p.grabacion.porTope = porTope;
    if (p.recorder && p.recorder.state !== "inactive") p.recorder.stop();
  }, []);

  const empezar = useCallback(async (soloVoz: boolean, ruta: string) => {
    const p = partes.current;
    let pantalla: MediaStream | null = null;
    try {
      if (!soloVoz) pantalla = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: { ideal: 15, max: 30 } }, audio: false });
    } catch {
      setEstado({ fase: "error", mensaje: "No se grabó: no elegiste qué pantalla compartir, o el navegador no lo dejó." });
      return;
    }
    const voz = await navigator.mediaDevices.getUserMedia({ audio: true }).catch(() => null);
    if (!pantalla && !voz) {
      setEstado({ fase: "error", mensaje: "No se grabó: el navegador no dejó usar el micrófono." });
      return;
    }
    const mezcla = new MediaStream([...(pantalla?.getVideoTracks() ?? []), ...(voz?.getAudioTracks() ?? [])]);
    const tipo = tipoPara(soloVoz);
    const recorder = new MediaRecorder(mezcla, { ...(tipo ? { mimeType: tipo } : {}), videoBitsPerSecond: VIDEO_BPS, audioBitsPerSecond: AUDIO_BPS });
    const grabacion = nuevaGrabacion(recorder.mimeType || tipo || (soloVoz ? "audio/webm" : "video/webm"), ruta);
    const { inicio } = grabacion;
    Object.assign(p, { recorder, streams: [pantalla, voz].filter((s): s is MediaStream => s !== null), trozos: [], grabacion });
    void anotar({ ...grabacion });
    recorder.ondataavailable = (e) => {
      if (e.data.size === 0) return;
      p.trozos.push(e.data);
      grabacion.ultimo = Date.now();
      void anotar({ ...grabacion }, { n: p.trozos.length - 1, blob: e.data });
    };
    // El último trozo llega antes de `onstop`: el archivo va completo.
    recorder.onstop = () => {
      const ms = Math.min(tope.current * 1000, Date.now() - inicio);
      grabacion.ms = ms;
      void anotar({ ...grabacion });
      soltar();
      p.recorder = null;
      void conDuracionReal(new Blob(p.trozos, { type: grabacion.tipo }), ms).then((archivo) =>
        setEstado({ fase: "grabada", id: grabacion.id, archivo, segundos: Math.round(ms / 1000), porTope: grabacion.porTope, ruta }),
      );
    };
    // «Dejar de compartir» del navegador también termina la grabación.
    pantalla?.getVideoTracks()[0]?.addEventListener("ended", () => detener());
    window.addEventListener("beforeunload", avisarAlSalir);
    recorder.start(TROZO_MS);
    setEstado({ fase: "grabando", segundos: 0, conVoz: voz !== null, soloVoz });
    p.reloj = setInterval(() => {
      const segundos = Math.floor((Date.now() - inicio) / 1000);
      if (segundos >= tope.current) detener(true);
      else setEstado((e) => (e.fase === "grabando" ? { ...e, segundos } : e));
    }, 1000);
  }, [detener, soltar]);

  /** Fuera la grabación: la guardó el servidor, o la persona la descartó. Sale también del cajón. */
  const descartar = useCallback(() => {
    soltar();
    const e = actual.current;
    if (e.fase === "grabada") void olvidar(e.id);
    setEstado({ fase: "lista" });
  }, [soltar]);

  // Al abrir el ERP: ¿quedó una grabación en el cajón (se recargó o se cerró)? Se ofrece completa.
  useEffect(() => {
    let vivo = true;
    void pendiente().then(async (r) => {
      if (!r || !vivo) return;
      const { grabacion: g, trozos } = r;
      const ms = Math.min(tope.current * 1000, g.ms ?? g.ultimo - g.inicio);
      const archivo = await conDuracionReal(new Blob(trozos, { type: g.tipo }), ms);
      if (!vivo) return;
      setEstado((e) => (e.fase !== "lista" ? e : {
        fase: "grabada", id: g.id, archivo, segundos: Math.round(ms / 1000), porTope: g.porTope, ruta: g.ruta,
        recuperada: g.ms === undefined ? "cortada" : "detenida",
      }));
    });
    return () => { vivo = false; };
  }, []);

  // Si la barra se desmonta (salir de la sesión) a media grabación, se suelta todo; lo grabado
  // queda en el cajón, detenido, para ofrecerlo la próxima vez.
  useEffect(() => () => {
    const p = partes.current;
    if (p.recorder && p.recorder.state !== "inactive") {
      p.recorder.onstop = null;
      p.recorder.stop();
      const g = p.grabacion;
      if (g) {
        g.ms = Math.min(tope.current * 1000, Date.now() - g.inicio);
        void anotar({ ...g });
      }
    }
    soltar();
  }, [soltar]);

  return { estado, empezar, detener, descartar };
}
